import { toaster } from '@/components/ui/toaster';
import { useInvitationData } from '@/contexts/invitation-context';
import { Button, Text, VStack } from '@chakra-ui/react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkInviteSubmit } from './use-bulk-invite-submit';

interface BulkLinkDeliveryViewProps {
  onClose: ({ success }: { success?: boolean }) => void;
  /** The whole selection: in link mode every creator is invitable (mail-less
   * ones are anchored on their social profile server-side). */
  totalCount: number;
}

/**
 * Link mode of the bulk affiliation delivery step: no email is sent, the batch
 * only creates the invitations so the brand hands the per-creator links over
 * itself. One explanatory clause and a "Create N invitations" CTA; the recap
 * (with the copyable links) shows on the `bulkProgress` epilogue once the batch
 * returns. Sends nothing but the delivery mode and the selection.
 */
const BulkLinkDeliveryView: React.FC<BulkLinkDeliveryViewProps> = memo(
  ({ onClose, totalCount }) => {
    const { t } = useTranslation();
    const { target, selectedProgram } = useInvitationData();
    const { submit, isSubmitting } = useBulkInviteSubmit();

    const handleCreate = useCallback(async () => {
      const isValidTarget = target?.type === 'wishlist-bulk';
      if (!isValidTarget || !selectedProgram?.id) {
        toaster.create({
          title: t('invitations.modal.invitationDetails.missingData'),
          type: 'error',
          duration: 3000,
        });
        return;
      }
      await submit({
        wishlistItemIds: target.data.map((item) => item.id),
        programId: selectedProgram.id,
        deliveryMode: 'link',
      });
    }, [target, selectedProgram, submit, t]);

    return (
      <VStack align="stretch" w="full" gap={4}>
        <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
          {t('invitations.modal.channel.bulk.linkExplainer')}
        </Text>
        <Button
          w="full"
          colorPalette="primary"
          onClick={handleCreate}
          loading={isSubmitting}
          disabled={totalCount === 0}
        >
          {t('invitations.modal.channel.bulk.createInvitations', { count: totalCount })}
        </Button>
      </VStack>
    );
  }
);

BulkLinkDeliveryView.displayName = 'BulkLinkDeliveryView';

export default BulkLinkDeliveryView;
